import { useInstantSearch } from 'react-instantsearch';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconPercent } from '@sorare/core/src/atoms/icons/system/iconPercent.ts';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { FilterTitle } from '@sorare/core/src/components/search/FilterTitle';
import { useFacet } from '@sorare/core/src/contexts/graphql-search';
import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { filters } from '@sorare/core/src/lib/glossary';

import {
  BackendRangeSlider,
  RangeSlider,
} from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';

const AppearancesTitle = () => {
  const { indexUiState } = useInstantSearch();

  const so5L5App = indexUiState.range?.[FILTERS.lastFiveAppearances.attribute];
  const so5L15App =
    indexUiState.range?.[FILTERS.lastFifteenAppearances.attribute];

  const selected =
    (so5L5App &&
    so5L5App !==
      `${FILTERS.lastFiveAppearances.min}:${FILTERS.lastFiveAppearances.max}`
      ? 1
      : 0) +
    (so5L15App &&
    so5L15App !==
      `${FILTERS.lastFifteenAppearances.min}:${FILTERS.lastFifteenAppearances.max}`
      ? 1
      : 0);

  return <FilterTitle name={filters.appearances} selected={selected} />;
};

const Section = styled(Vertical)`
  &:not(:has(.FilterWidget.visible)) {
    display: none;
  }
`;

const AppearancesComponent = ({
  fiveAppearancesWidget,
  fifteenAppearancesWidget,
  visible,
}: {
  fiveAppearancesWidget: FilterWidget;
  fifteenAppearancesWidget: FilterWidget;
  visible: boolean;
}) => {
  return (
    <Vertical gap={3}>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...FILTERS.lastFiveAppearances.title} />
        </LabelM>
        {fiveAppearancesWidget.Component({ visible })}
      </Section>
      <Section>
        <LabelM color="var(--c-nd-600)">
          <FormattedMessage {...FILTERS.lastFifteenAppearances.title} />
        </LabelM>
        {fifteenAppearancesWidget.Component({ visible })}
      </Section>
    </Vertical>
  );
};

export const RefineAppearances = (): FilterWidget => {
  const interval5 = 100 / FILTERS.lastFiveAppearances.max;
  const interval15 = 100 / FILTERS.lastFifteenAppearances.max;

  const fiveAppearancesWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFiveAppearances.attribute}
        minForced={FILTERS.lastFiveAppearances.min}
        maxForced={FILTERS.lastFiveAppearances.max}
        toInput={v => Math.round(v * interval5).toString()}
        fromInput={v => v / interval5}
        icon={<FontAwesomeIcon icon={iconPercent} />}
        visible={visible}
      />
    ),
    FILTERS.lastFiveAppearances
  );

  const fifteenAppearancesWidget = makeRangeFilter(
    ({ visible }) => (
      <RangeSlider
        attribute={FILTERS.lastFifteenAppearances.attribute}
        minForced={FILTERS.lastFifteenAppearances.min}
        maxForced={FILTERS.lastFifteenAppearances.max}
        toInput={v => Math.round(v * interval15).toString()}
        fromInput={v => v / interval15}
        icon={<FontAwesomeIcon icon={iconPercent} />}
        visible={visible}
      />
    ),
    FILTERS.lastFifteenAppearances
  );

  return {
    key: FILTERS.appearances.attribute,
    type: 'range',
    attribute: FILTERS.appearances.attribute,
    title: <AppearancesTitle />,
    Component: ({ visible }) => (
      <AppearancesComponent
        fiveAppearancesWidget={fiveAppearancesWidget}
        fifteenAppearancesWidget={fifteenAppearancesWidget}
        visible={visible}
      />
    ),
  };
};

const BackendAppearancesTitle = () => {
  const { selected: selectedL5 } = useFacet('so5L5Avg');
  const { selected: selectedL15 } = useFacet('so5L15Avg');

  return (
    <FilterTitle
      name={FILTERS.appearances.title}
      selected={[selectedL5, selectedL15].filter(Boolean).length}
    />
  );
};

export const RefineBackendAppearances = (): FilterWidget => {
  const fiveAppearancesWidget = {
    key: 'so5L5Appearances',
    type: 'range',
    attribute: 'so5L5App',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L5App" />
    ),
  } satisfies FilterWidget;

  const fifteenAppearancesWidget = {
    key: 'so5L15Appearances',
    type: 'range',
    attribute: 'so5L15App',
    Component: ({ visible }) => (
      <BackendRangeSlider visible={visible} attribute="so5L15App" />
    ),
  } satisfies FilterWidget;

  return {
    key: FILTERS.appearances.attribute,
    type: 'range',
    attribute: FILTERS.appearances.attribute,
    subAttributes: ['so5L5App', 'so5L15App'],
    title: <BackendAppearancesTitle />,
    Component: ({ visible }) => (
      <AppearancesComponent
        fiveAppearancesWidget={fiveAppearancesWidget}
        fifteenAppearancesWidget={fifteenAppearancesWidget}
        visible={visible}
      />
    ),
  };
};
